import { useStores } from '@littlebird/sdk';
import {
  buildSharedArenaUrl,
  buildSharedChatUrl,
  copyShareLink,
  type NormalizedShareStatus,
  type ShareLinkAdapter,
  useShareLink,
  type UseShareLinkReturn,
} from '@littlebird/ui-utils';
import { type ReactNode, useMemo, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { createShareNotify } from '@/components/blocks/share/share-notify';
import { ShareAccessControls } from '@/components/blocks/share/ShareAccessControls';
import { SharePopover } from '@/components/blocks/share/SharePopover';
import { ShareSocialButtons } from '@/components/blocks/share/ShareSocialButtons';
import { type ModalFC } from '@/components/modals/modalsManager';
import { Text } from '@/components/ui';
import { Dialog } from '@/components/ui/dialog';
import { getEnvVar } from '@/env';
import { FEATURES, useFeature } from '@/features/feature-flags';
import { userStore } from '@/stores/user.store';
import { trackEvent } from '@/utils/analytics';

import { teamStore } from '../team/team.store';

import { SharedChatType } from './shared-chat/shared-chat.types';

const BASE_URL = getEnvVar('BASE_URL_FOR_PUBLIC_LINKS');

const notify = createShareNotify(
  'Failed to share chats',
  'Failed to share chats, please try again'
);

type ShareChatContext = UseShareLinkReturn & { type: SharedChatType };

const SHARE_CHAT_TITLE = 'Share chat';
const SHARE_CHAT_SUBTITLE = 'Only messages up until now will be shared';

function useShareChat(chatIds: string[], type: SharedChatType, open: boolean) {
  const supportsTeam = type !== SharedChatType.Arena;
  const canShareBeyondPrivate = !userStore.hasSignedBaa;
  const hasTeam = !!teamStore.dashboard;
  const baseUrl = BASE_URL ?? window.location.origin;
  const { threads } = useStores();
  const canInvitePeople = useFeature()(FEATURES.SHARE_WITH_PEOPLE);

  const adapter = useMemo<ShareLinkAdapter>(
    () => ({
      async getStatus(id: string): Promise<NormalizedShareStatus> {
        return threads.getShareStatus(id);
      },
      async setVisibility(id, visibility) {
        return threads.setChatVisibility(id, visibility);
      },
      async revoke(id) {
        await threads.revokeShareToken(id);
      },
      invites: canInvitePeople
        ? {
            add: (id, email) => threads.addChatShareInvite(id, email),
            remove: (id, email) => threads.removeChatShareInvite(id, email),
          }
        : undefined,
      buildUrl(ids, tokens) {
        if (type === SharedChatType.Arena) {
          return buildSharedArenaUrl(ids, tokens, baseUrl);
        }
        return buildSharedChatUrl(ids[0]!, tokens[0]!, baseUrl);
      },
      onLinkCreated(url) {
        return copyShareLink(url, notify);
      },
      onShared(ids, _visibility) {
        trackEvent('CHAT_SHARED', {
          type,
          ...(type === SharedChatType.Arena
            ? { chatIds: ids }
            : { chatId: ids[0] }),
        });
      },
      onUnshared(ids) {
        trackEvent('CHAT_UNSHARED', {
          type,
          ...(type === SharedChatType.Arena
            ? { chatIds: ids }
            : { chatId: ids[0] }),
        });
      },
      ...notify,
    }),
    [type, baseUrl, threads, canInvitePeople]
  );

  const link = useShareLink({
    ids: chatIds,
    adapter,
    open,
    supportsTeam,
    canShareBeyondPrivate,
    hasTeam,
  });

  return { type, ...link };
}

const Content = ({ ctx }: { ctx: ShareChatContext }) => (
  <ShareAccessControls
    link={ctx}
    idPrefix="share-chat"
    linkFooter={
      <>
        <NewMessagesBanner ctx={ctx} />
        {ctx.shareStatus?.visibility === 'public' && ctx.url && (
          <ShareSocialButtons url={ctx.url} />
        )}
      </>
    }
  />
);

const NewMessagesBanner = ({ ctx }: { ctx: ShareChatContext }) => {
  const { type, shareStatus, url, isCreating, handleCreateOrUpdateLink } = ctx;

  if (type === SharedChatType.Arena) return null;
  if (!shareStatus?.isShared || !url || !shareStatus.hasNewMessages)
    return null;

  const newMsgCount =
    (shareStatus.currentMsgLength ?? 0) - (shareStatus.sharedMsgLength ?? 0);

  return (
    <Text size="xs" variant="muted">
      {newMsgCount} new {newMsgCount === 1 ? 'message' : 'messages'} since last
      shared.{' '}
      <button
        type="button"
        className="inline-flex items-center gap-1 text-foreground underline underline-offset-4 hover:text-foreground/80 transition-colors disabled:opacity-50"
        onClick={() =>
          handleCreateOrUpdateLink(
            shareStatus.visibility === 'private'
              ? 'public'
              : shareStatus.visibility
          )
        }
        disabled={isCreating}
      >
        {isCreating ? (
          <>
            <Icons.loader className="size-3 animate-spin shrink-0" />
            Updating...
          </>
        ) : (
          <>
            <Icons.refresh className="size-3 shrink-0" />
            Update
          </>
        )}
      </button>
    </Text>
  );
};

type ShareChatPopoverProps = {
  chatIds: Array<string>;
  type?: SharedChatType;
  children: ReactNode;
};

export const ShareChatPopover = ({
  chatIds,
  type = SharedChatType.Chat,
  children,
}: ShareChatPopoverProps) => {
  const [open, setOpen] = useState(false);
  const ctx = useShareChat(chatIds, type, open);

  return (
    <SharePopover
      anchor={children}
      open={open}
      onOpenChange={setOpen}
      title={SHARE_CHAT_TITLE}
      description={SHARE_CHAT_SUBTITLE}
    >
      <Content ctx={ctx} />
    </SharePopover>
  );
};

type ShareChatModalProps = {
  chatIds: Array<string>;
  type?: SharedChatType;
};

export const ShareChatModal: ModalFC<ShareChatModalProps> = ({
  chatIds,
  open,
  onOpenChange,
  type = SharedChatType.Chat,
}) => {
  const ctx = useShareChat(chatIds, type, open);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className="overflow-hidden max-w-sm gap-3 px-4 pt-3.5 pb-0">
        <Dialog.Header className="space-y-0.5 pr-8">
          <Dialog.Title className="text-sm font-medium">
            {SHARE_CHAT_TITLE}
          </Dialog.Title>
          <Dialog.Description className="text-xs">
            {SHARE_CHAT_SUBTITLE}
          </Dialog.Description>
        </Dialog.Header>
        <Content ctx={ctx} />
      </Dialog.Content>
    </Dialog.Root>
  );
};
